import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { getContentHeight, showType } from "../../../../../utils/textUtils";
import useIsMobile from "../../../../../hooks/useIsMobile";
import { pickResumeParagraph } from "../../common/EntrySelector";
import { bookTitleOf } from "../bookScreen";
import WorkbookCover from "../../comprehensive/book/WorkbookCover";
import "./BookEntrySelector.css";

// 리더스북 · 파닉스북 과제의 첫 화면(2026-10-20 대표: "책 표지 보여 주고 메뉴 고르게"). 일반 과제의 EntrySelector 와
// 하는 일은 같다(이어할 파트 자동 선택, 끝낸 파트 막기, 섹션 누르면 바로 시작). 생김새만 책답게:
// 짙은 책 색 바탕에 표지를 받침 위에 세우고, 오른쪽 흰 판에 진도 고리 · 도장 줄 · 색깔 메뉴 단추를 둔다.
// 가만히 있을 때는 움직이지 않고 누를 때만 반응한다(2026-10-20 대표: 정신없다 → 표지 보이고 메뉴 고르게, 유아가 봐도 괜찮게).
const DEFAULT_ACCENT = "#D6336C";
// 원서 워크북(내용이해 mode=book, 2026-10-24 대표: "목록도 리더스처럼")은 내용이해 화면 색
const WORKBOOK_ACCENT = "#E8920A";

// 섹션 유형 · 단계마다 아이콘 이름(아래 StepIcon)
const iconOf = (section) => {
  const step = section.question?.phonics_step;
  if (step === "mouth") return "mouth";
  if (step === "story") return "headphones";
  if (step === "speak") return "mic";
  if (step === "quiz" || step === "ipa_quiz") return "star";
  if (section.type === "study") return "cards";
  if (section.type === "matching") return "puzzle";
  if (section.type === "exam") return "star";
  if (section.type === "syntax_analysis") return "blocks";
  if (/문제/.test(section.type_name || "")) return "pencil";
  if (/프로젝트/.test(section.type_name || "")) return "palette";
  return "book";
};

// 아이콘마다 제 색(유아도 색으로 메뉴를 구분하게)
const HUE_OF_ICON = {
  mouth: "#F0568C",
  headphones: "#2F9BE8",
  mic: "#F2643C",
  star: "#F5A623",
  cards: "#8C5BE8",
  puzzle: "#18B08A",
  blocks: "#3F7BF0",
  pencil: "#4DB33D",
  palette: "#E8508F",
  book: "#14A5B8",
};

// 표지: 책 표지(question.book.cover_url, 출제 때 옮겨 둔 첫 쪽). 없으면(예전 과제) 쪽 그림이 있는 섹션의 첫 쪽
const coverOf = (sectionList) => {
  const cover = sectionList.map((section) => section.question?.book?.cover_url).find(Boolean);
  if (cover) return cover;
  for (const section of sectionList) {
    const page = (section.question?.pages || []).find((one) => one.image);
    if (page) return page.image;
  }
  for (const section of sectionList) {
    const picture = section.question?.book?.picture_url;
    if (picture) return picture;
  }
  return "";
};

// 지문(parts)이 붙은 시험 대비 워크북은 '내용이해', 원서 한 장짜리는 원래 이름(원서 워크북)
const sectionNameOf = (assignmentType, section) =>
  section.type === "book" && (section.question?.data?.parts || []).length > 0
    ? "내용이해"
    : showType(assignmentType, section.type);

const bookOf = (sectionList) =>
  sectionList.map((section) => section.question?.book).find((book) => book && (book.title || book.theme_color)) || {};

const BookEntrySelector = ({
  entryReady = true,
  paragraphList = [],
  sectionList = [],
  selectedParagraph,
  onSelectParagraph,
  onEnterSection,
  isPreview = false,
  isReview = false,
}) => {
  const { assignment, sectionListStatus } = useSelector((state) => state.assignment);
  const isMobile = useIsMobile();
  const loadingSections = sectionListStatus === "loading" || sectionListStatus === "idle";

  const sortedParagraphs = paragraphList.slice().sort((a, b) => {
    if (a.is_finished && !b.is_finished) return 1;
    if (!a.is_finished && b.is_finished) return -1;
    return 0;
  });

  // 들어오면 이어할 파트를 펼친다(EntrySelector 와 같은 규칙)
  const autoSelected = useRef(false);
  useEffect(() => {
    if (!entryReady || autoSelected.current) return;
    if (selectedParagraph) {
      autoSelected.current = true;
      if (sectionListStatus === "idle") onSelectParagraph(selectedParagraph);
      return;
    }
    if (!sortedParagraphs.length) return;
    const first = pickResumeParagraph(sortedParagraphs, isReview || isPreview);
    if (first) {
      autoSelected.current = true;
      onSelectParagraph(first);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paragraphList, entryReady]);

  // 누르면 단추가 쏙 들어갔다 나온 뒤 넘어간다(2026-10-20 대표: 눌렀을 때 반응만 있으면 된다, 유아가 봐도 괜찮게)
  const [pressedId, setPressedId] = useState(null);
  const pressSection = (section) => {
    if (pressedId) return;
    setPressedId(section.id);
    setTimeout(() => {
      setPressedId(null);
      onEnterSection(section);
    }, 220);
  };

  const sortedSections = sectionList.slice().sort((a, b) => a.id - b.id);
  const nextId = sortedSections.find((section) => !section.is_finished)?.id;
  const doneCount = sortedSections.filter((section) => section.is_finished).length;
  // 원서 워크북은 표지 그림이 없어 글자 표지(WorkbookCover)를 세운다
  const workbook = sortedSections.find((section) => section.type === "book")?.question?.data || null;
  const book = workbook
    ? { title: workbook.book_title || assignment?.title, theme_color: WORKBOOK_ACCENT }
    : bookOf(sortedSections);
  const accent = book.theme_color || DEFAULT_ACCENT;
  const cover = workbook ? "" : coverOf(sortedSections);
  const title = bookTitleOf(book.title || assignment?.title);
  const series = bookTitleOf(book.series);
  const percent = sortedSections.length ? Math.round((doneCount / sortedSections.length) * 100) : 0;

  return (
    <div
      className={`book-entry${isMobile ? " is-mobile" : ""}`}
      style={{ height: getContentHeight(isMobile), "--book-accent": accent }}
    >
      {cover && <div className="book-entry__backdrop" style={{ backgroundImage: `url("${cover}")` }} aria-hidden="true" />}
      <aside className="book-entry__shelf">
        <div className="book-entry__cover-wrap">
          {workbook ? (
            <div className="book-entry__cover book-entry__cover--workbook">
              <WorkbookCover book={workbook} title={book.title} compact />
            </div>
          ) : cover ? (
            <img className="book-entry__cover" src={cover} alt="" />
          ) : (book.symbols || []).length > 0 ? (
            // ECHO 발음기호는 표지가 없다. 그 과제의 기호를 표지처럼 늘어놓는다(api assignment/ipa_course.py)
            <div className="book-entry__cover book-entry__cover--symbols">
              <span>{book.series}</span>
              <div>
                {book.symbols.map((symbol) => (
                  <b key={symbol}>/{symbol}/</b>
                ))}
              </div>
            </div>
          ) : (
            <div className="book-entry__cover is-empty" />
          )}
        </div>
        <div className="book-entry__title">
          {series && <span className="book-entry__series">{series}</span>}
          <h2>{title}</h2>
        </div>
        {sortedParagraphs.length > 1 && (
          <div className="book-entry__parts">
            {sortedParagraphs.map((paragraph) => {
              const isOn = selectedParagraph?.id === paragraph.id;
              const isLocked = !isReview && !isPreview && paragraph.is_finished;
              return (
                <button
                  type="button"
                  key={paragraph.id}
                  className={`book-entry__part${isOn ? " is-on" : ""}${paragraph.is_finished ? " is-done" : ""}`}
                  onClick={() => !isLocked && onSelectParagraph(paragraph)}
                >
                  {paragraph.is_finished ? "✓ " : ""}
                  {paragraph.title}
                </button>
              );
            })}
          </div>
        )}
      </aside>

      <section className="book-entry__board">
        {(!entryReady || (selectedParagraph && loadingSections)) && (
          <div className="book-entry__loading">
            {[0, 1, 2, 3].map((index) => (
              <span key={index} />
            ))}
          </div>
        )}
        {entryReady && !loadingSections && sortedSections.length === 0 && (
          <p className="book-entry__empty">이 파트에는 학습할 내용이 없어요. 선생님께 알려 주세요.</p>
        )}
        {entryReady && !loadingSections && sortedSections.length > 0 && (
          <>
            <div className="book-entry__status">
              <ProgressRing percent={percent} />
              <ol className="book-entry__stamps" aria-label={`${doneCount} / ${sortedSections.length}`}>
                {sortedSections.map((section) => (
                  <li
                    key={section.id}
                    className={`${section.is_finished ? "is-done" : ""}${section.id === nextId ? " is-next" : ""}`}
                  >
                    {section.is_finished && (
                      <svg viewBox="0 0 24 24" aria-hidden="true">
                        <path d="M6 12.5l4 4L18 8" />
                      </svg>
                    )}
                  </li>
                ))}
              </ol>
            </div>
            <div className="book-entry__menu">
              {sortedSections.map((section, index) => {
                const isDone = section.is_finished;
                const isNext = section.id === nextId;
                const icon = iconOf(section);
                return (
                  <button
                    type="button"
                    key={section.id}
                    className={`book-entry__tile${isDone ? " is-done" : ""}${isNext ? " is-next" : ""}${pressedId === section.id ? " is-pressed" : ""}`}
                    style={{ "--tile-hue": HUE_OF_ICON[icon] }}
                    onClick={() => pressSection(section)}
                  >
                    <span className="book-entry__icon">
                      <StepIcon name={icon} />
                      {isDone && (
                        <i className="book-entry__done-mark">
                          <svg viewBox="0 0 24 24" aria-hidden="true">
                            <path d="M6 12.5l4 4L18 8" />
                          </svg>
                        </i>
                      )}
                    </span>
                    <span className="book-entry__name">
                      <small>{String(index + 1).padStart(2, "0")}</small>
                      {section.type_name || sectionNameOf(assignment?.type, section)}
                    </span>
                    {isNext ? (
                      <span className="book-entry__go">{doneCount ? "이어서" : "시작"}</span>
                    ) : (
                      <span className="book-entry__chevron" aria-hidden="true">
                        <svg viewBox="0 0 24 24">
                          <path d="M10 7l5 5-5 5" />
                        </svg>
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </>
        )}
      </section>
    </div>
  );
};

const RING_RADIUS = 30;
const RING_LENGTH = 2 * Math.PI * RING_RADIUS;

const ProgressRing = ({ percent }) => (
  <div className="book-entry__ring">
    <svg viewBox="0 0 72 72" aria-hidden="true">
      <circle cx="36" cy="36" r={RING_RADIUS} className="book-entry__ring-track" />
      <circle
        cx="36"
        cy="36"
        r={RING_RADIUS}
        className="book-entry__ring-fill"
        strokeDasharray={RING_LENGTH}
        strokeDashoffset={RING_LENGTH * (1 - percent / 100)}
      />
    </svg>
    <b>
      {percent}
      <small>%</small>
    </b>
  </div>
);

// ECHO 교재 톤: 파스텔 바탕에 같은 색 진한 잉크 선, 면은 그 사이 색으로 칠한다(구문분석 단추와 같은 말씨).
// soft: 칠하는 면, paper: 흰 종이(겹친 카드 앞장), line: 잉크 선
const ICON_LAYER = {
  book: {
    soft: "M4 6c3-1.3 5.5-1.3 8 .5v12.5c-2.5-1.8-5-1.8-8-.5zM12 6.5c2.5-1.8 5-1.8 8-.5v12c-3-1.3-5.5-1.3-8 .5z",
    line: "M4 6c3-1.3 5.5-1.3 8 .5 2.5-1.8 5-1.8 8-.5v12c-3-1.3-5.5-1.3-8 .5-2.5-1.8-5-1.8-8-.5zM12 6.5v12.5",
  },
  cards: {
    soft: "M8.5 3.5h11v12.5h-11z",
    paper: "M4.5 7.5h11V20h-11z",
    line: "M8.5 7.5V3.5h11v12.5h-4M4.5 7.5h11V20h-11z",
  },
  puzzle: {
    soft: "M5 9h3a2 2 0 1 1 4 0h3v3a2 2 0 1 1 0 4v3H5v-3a2 2 0 1 0 0-4z",
    line: "M5 9h3a2 2 0 1 1 4 0h3v3a2 2 0 1 1 0 4v3H5v-3a2 2 0 1 0 0-4z",
  },
  star: {
    soft: "M12 3.5l2.6 5.4 5.9.8-4.3 4.1 1 5.8L12 16.9l-5.2 2.7 1-5.8-4.3-4.1 5.9-.8z",
    line: "M12 3.5l2.6 5.4 5.9.8-4.3 4.1 1 5.8L12 16.9l-5.2 2.7 1-5.8-4.3-4.1 5.9-.8z",
  },
  blocks: {
    soft: "M4 13h7v7H4zM8.5 4h7v7h-7z",
    paper: "M13 13h7v7h-7z",
    line: "M4 13h7v7H4zM13 13h7v7h-7zM8.5 4h7v7h-7z",
  },
  pencil: {
    soft: "M5 19l1.2-4.4L15.5 5.3l3.2 3.2-9.3 9.3z",
    line: "M5 19l1.2-4.4L15.5 5.3l3.2 3.2-9.3 9.3zM13.6 7.2l3.2 3.2",
  },
  palette: {
    soft: "M12 4a8 8 0 1 0 0 16c1.2 0 1.6-1 1-1.9-.7-1 0-2.1 1.2-2.1H17a3 3 0 0 0 3-3c0-5-3.6-9-8-9z",
    line: "M12 4a8 8 0 1 0 0 16c1.2 0 1.6-1 1-1.9-.7-1 0-2.1 1.2-2.1H17a3 3 0 0 0 3-3c0-5-3.6-9-8-9zM8.2 11h.01M11 7.8h.01M15 8.4h.01",
  },
  mouth: {
    soft: "M4 12c2.5-3 5-4 8-4s5.5 1 8 4c-2.5 3-5 4-8 4s-5.5-1-8-4z",
    line: "M4 12c2.5-3 5-4 8-4s5.5 1 8 4c-2.5 3-5 4-8 4s-5.5-1-8-4zM6.5 12h11",
  },
  headphones: {
    soft: "M5 14h3v6H5zM16 14h3v6h-3z",
    line: "M5 15v-3a7 7 0 0 1 14 0v3M5 14h3v6H5zM16 14h3v6h-3z",
  },
  mic: {
    soft: "M9 4h6v9a3 3 0 0 1-6 0z",
    line: "M9 4h6v9a3 3 0 0 1-6 0zM6 11a6 6 0 0 0 12 0M12 17v3M9 20h6",
  },
};

const StepIcon = ({ name }) => {
  const layer = ICON_LAYER[name] || ICON_LAYER.book;
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      {layer.soft && <path className="book-entry__icon-soft" d={layer.soft} />}
      {layer.paper && <path className="book-entry__icon-paper" d={layer.paper} />}
      <path className="book-entry__icon-line" d={layer.line} />
    </svg>
  );
};

export default BookEntrySelector;
